/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import { Input } from "@/components/new/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { mergeAttributes, Node } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion from "@tiptap/suggestion";
import { Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import tippy from "tippy.js";

type CommandType = "search" | "element";
type SearchType = "product" | "company";

interface BaseCommand {
	id: string;
	title: string;
	type: CommandType;
	description?: string;
	icon?: React.ReactNode;
}

interface SearchCommand extends BaseCommand {
	type: "search";
	searchType: SearchType;
}

interface ElementCommand extends BaseCommand {
	type: "element";
	execute: (editor: any) => void;
}

type Command = SearchCommand | ElementCommand;

interface SearchResultItem {
	id: string;
	label: string;
	type: SearchType;
	metadata?: {
		slug?: string;
		companySlug?: string;
		[key: string]: any;
	};
}

const SearchDialog = ({
	isOpen,
	onClose,
	searchType,
	onSelect,
}: {
	isOpen: boolean;
	onClose: () => void;
	searchType: SearchType;
	onSelect: (item: SearchResultItem) => void;
}) => {
	const [query, setQuery] = React.useState("");
	const [results, setResults] = React.useState<SearchResultItem[]>([]);
	const [loading, setLoading] = React.useState(false);

	const search = React.useCallback(
		async (searchQuery: string) => {
			if (!searchQuery.trim()) {
				setResults([]);
				return;
			}

			setLoading(true);
			try {
				const items =
					searchType === "product"
						? await searchProducts(searchQuery)
						: await searchCompanies(searchQuery);
				setResults(items);
			} catch (error) {
				console.error("Search failed:", error);
				setResults([]);
			} finally {
				setLoading(false);
			}
		},
		[searchType]
	);

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			search(query);
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [query, search]);

	return (
		<Dialog open={isOpen} onOpenChange={() => onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Search {searchType}</DialogTitle>
				</DialogHeader>
				<div className="flex items-center space-x-2">
					<div className="relative flex-1">
						<Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
						<Input
							placeholder={`Search ${searchType}...`}
							className="pl-8"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
					</div>
				</div>
				<div className="mt-4 max-h-60 overflow-y-auto">
					{loading ? (
						<div className="p-4 text-center text-gray-500">Loading...</div>
					) : results.length === 0 ? (
						<div className="p-4 text-center text-gray-500">
							{query ? "No results found" : "Start typing to search"}
						</div>
					) : (
						<div className="space-y-2">
							{results.map((result) => (
								<button
									key={result.id}
									className="w-full rounded-md p-2 text-left hover:bg-gray-100"
									onClick={() => {
										onSelect(result);
										onClose();
									}}
								>
									<div className="font-medium">{result.label}</div>
									{result.metadata?.companySlug && (
										<div className="text-sm text-gray-500">{result.metadata.companySlug}</div>
									)}
								</button>
							))}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

const CommandList = ({
	items,
	command,
}: {
	items: Command[];
	command: (item: Command) => void;
}) => {
	const [selectedIndex, setSelectedIndex] = useState(0);

	const selectItem = useCallback(
		(index: number) => {
			const item = items[index];
			if (item) {
				command(item);
			}
		},
		[command, items]
	);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowUp") {
				e.preventDefault();
				setSelectedIndex((selectedIndex + items.length - 1) % items.length);
			}

			if (e.key === "ArrowDown") {
				e.preventDefault();
				setSelectedIndex((selectedIndex + 1) % items.length);
			}

			if (e.key === "Enter") {
				e.preventDefault();
				selectItem(selectedIndex);
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [selectedIndex, selectItem, items]);

	return (
		<div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
			{items.map((item, index) => (
				<button
					key={item.id}
					onClick={() => selectItem(index)}
					className={cn(
						"flex w-full items-center space-x-2 rounded p-2 text-left",
						index === selectedIndex ? "bg-gray-100" : ""
					)}
				>
					{item.icon && <span className="h-4 w-4">{item.icon}</span>}

					<div>
						<div className="font-medium">{item.title}</div>
						{item.description && <div className="text-sm text-gray-500">{item.description}</div>}
					</div>
				</button>
			))}
		</div>
	);
};

// TODO: Make an API call to get the products
const searchProducts = async (query: string): Promise<SearchResultItem[]> => {
	console.log("searchProducts", query);

	return [
		{
			id: "1",
			label: "Sample Product",
			type: "product",
			metadata: {
				slug: "sample-product",
				companySlug: "sample-company",
			},
		},
	];
};

// TODO: Make an API call to get the companies
const searchCompanies = async (query: string): Promise<SearchResultItem[]> => {
	console.log("searchCompanies", query);

	return [
		{
			id: "1",
			label: "Sample Company",
			type: "company",
			metadata: {
				slug: "sample-company",
			},
		},
	];
};

export const SlashCommandPluginKey = new PluginKey("slash-command");

export const SlashCommand = Node.create({
	name: "slashCommand",

	addOptions() {
		return {
			suggestion: {
				char: "/",
				command: ({ editor, range, props }: { editor: any; range: any; props: Command }) => {
					if (props.type === "search") {
						const suggestionMenu = document.querySelector("[data-tippy-root]");

						if (suggestionMenu) {
							suggestionMenu.remove();
						}

						const portal = document.createElement("div");

						document.body.appendChild(portal);

						const searchDialog = new ReactRenderer(SearchDialog, {
							props: {
								isOpen: true,
								searchType: props.searchType,
								onClose: () => {
									searchDialog.destroy();
									document.body.removeChild(portal);
								},
								onSelect: (item: SearchResultItem) => {
									editor
										.chain()
										.focus()
										.deleteRange(range)
										.insertContent([
											{
												type: "slashCommand",
												attrs: {
													id: item.id,
													label: item.label,
													type: item.type,
													metadata: item.metadata,
												},
											},
											{
												type: "text",
												text: " ",
											},
										])
										.run();
								},
							},
							editor,
						});

						portal.appendChild(searchDialog.element);
					} else if (props.type === "element") {
						editor.chain().focus().deleteRange(range).run();
						props.execute(editor);
					}
				},
				pluginKey: SlashCommandPluginKey,
				items: ({ query }: { query: string }) => {
					const commands: Command[] = [
						{
							id: "product",
							title: "Search Products",
							description: "Search and mention a product",
							type: "search",
							searchType: "product",
							icon: <Search className="h-4 w-4" />,
						},
						{
							id: "company",
							title: "Search Companies",
							description: "Search and mention a company",
							type: "search",
							searchType: "company",
							icon: <Search className="h-4 w-4" />,
						},
					];

					return commands.filter((item) => item.title.toLowerCase().includes(query.toLowerCase()));
				},
				render: () => {
					let component: ReactRenderer | null = null;
					let popup: any | null = null;

					return {
						onStart: (props: { editor: any; clientRect: any }) => {
							component = new ReactRenderer(CommandList, {
								props,
								editor: props.editor,
							});

							popup = tippy("body", {
								getReferenceClientRect: props.clientRect,
								appendTo: () => document.body,
								content: component.element,
								showOnCreate: true,
								interactive: true,
								trigger: "manual",
								placement: "bottom-start",
							});
						},
						onUpdate: (props: { editor: any; clientRect: any }) => {
							component?.updateProps(props);

							popup?.[0].setProps({
								getReferenceClientRect: props.clientRect,
							});
						},
						onKeyDown: (props: { event: KeyboardEvent }) => {
							if (props.event.key === "Escape") {
								popup?.[0].hide();
								return true;
							}

							return false;
						},
						onExit: () => {
							popup?.[0].destroy();
							component?.destroy();
						},
					};
				},
			},
		};
	},

	group: "inline",
	inline: true,
	selectable: false,
	atom: true,

	addAttributes() {
		return {
			id: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-id"),
				renderHTML: (attributes) => {
					if (!attributes.id) return {};
					return { "data-id": attributes.id };
				},
			},
			label: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-label"),
				renderHTML: (attributes) => {
					if (!attributes.label) return {};
					return { "data-label": attributes.label };
				},
			},
			type: {
				default: null,
				parseHTML: (element) => element.getAttribute("data-type"),
				renderHTML: (attributes) => {
					if (!attributes.type) return {};
					return { "data-type": attributes.type };
				},
			},
			metadata: {
				default: null,
				parseHTML: (element) => {
					const metadata = element.getAttribute("data-metadata");
					return metadata ? JSON.parse(metadata) : null;
				},
				renderHTML: (attributes) => {
					if (!attributes.metadata) return {};
					return { "data-metadata": JSON.stringify(attributes.metadata) };
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ node, HTMLAttributes }) {
		const type = node.attrs.type as SearchType;
		const metadata = node.attrs.metadata || {};

		let href = "#";
		if (type === "product" && metadata.companySlug && metadata.slug) {
			href = `/companies/${metadata.companySlug}/products/${metadata.slug}`;
		} else if (type === "company" && metadata.slug) {
			href = `/companies/${metadata.slug}`;
		}

		return [
			"a",
			mergeAttributes(
				{
					"data-type": this.name,
					href: href,
					class: `content-mention mention-${type}`,
				},
				HTMLAttributes
			),
			"@" + (node.attrs.label || node.attrs.id),
		];
	},

	renderText({ node }) {
		return node.attrs.label || node.attrs.id;
	},

	addProseMirrorPlugins() {
		return [
			Suggestion({
				editor: this.editor,
				...this.options.suggestion,
			}),
		];
	},
});
